import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { isCacheValid } from '../cacheUtils';

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().users;
    if (isCacheValid(state.lastFetched)) {
      return { data: state.data, cached: true };
    }
    try {
      const response = await Axios.get('/api/user');
      return { data: response.data.data, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const fetchUserDetail = createAsyncThunk(
  'users/fetchUserDetail',
  async (userId, { getState, rejectWithValue }) => {
    const state = getState().users;
    const cacheKey = `detail_${userId}`;
    if (isCacheValid(state.detailLastFetched[cacheKey])) {
      return { data: state.userDetails[cacheKey], cacheKey, cached: true };
    }
    try {
      const response = await Axios.get(`/api/user/${userId}`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch user detail');
    }
  }
);

export const fetchUserEmails = createAsyncThunk(
  'users/fetchUserEmails',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().users;
    if (isCacheValid(state.emailsLastFetched)) {
      return { data: state.emails, cached: true };
    }
    try {
      const response = await Axios.get('/api/user/emails');
      return { data: response.data.data, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch user emails');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    data: null,
    userDetails: {},
    emails: null,
    loading: false,
    detailLoading: false,
    error: null,
    lastFetched: null,
    detailLastFetched: {},
    emailsLastFetched: null,
  },
  reducers: {
    invalidateCache: (state) => {
      state.lastFetched = null;
      state.detailLastFetched = {};
      state.emailsLastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        if (!state.data) state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        if (!action.payload.cached) {
          state.data = action.payload.data;
          state.lastFetched = Date.now();
        }
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserDetail.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchUserDetail.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.userDetails[cacheKey] = data;
          state.detailLastFetched[cacheKey] = Date.now();
        }
        state.detailLoading = false;
      })
      .addCase(fetchUserDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserEmails.pending, (state) => {
        // silent load
      })
      .addCase(fetchUserEmails.fulfilled, (state, action) => {
        if (!action.payload.cached) {
          state.emails = action.payload.data;
          state.emailsLastFetched = Date.now();
        }
      })
      .addCase(fetchUserEmails.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { invalidateCache: invalidateUsersCache } = usersSlice.actions;
export default usersSlice.reducer;